import { useEffect, useState } from 'react';
import { Loader2, Wand2, X } from 'lucide-react';
import { useAuth } from '../auth/store';

interface AiModel {
  id: string;
  name: string;
  type: string;
  max_tokens?: number;
}

interface AiDialogProps {
  open: boolean;
  onClose: () => void;
  onResult: (code: string) => void;
  onOpenSettings: () => void;
}

const PRESETS: Array<{ label: string; prompt: string }> = [
  { label: 'AWS 3-tier web app', prompt: 'C4 Container diagram of a 3-tier web application on AWS with ALB, ECS frontend, ECS backend, RDS Postgres, S3 static assets.' },
  { label: 'OAuth2 login flow', prompt: 'Sequence diagram for an OAuth2 Authorization Code flow with PKCE involving User, Browser, App, Auth Server, Resource Server.' },
  { label: 'Order state machine', prompt: 'State diagram for an e-commerce order: Created, Paid, Fulfilled, Shipped, Delivered, Cancelled, Refunded.' },
  { label: 'ETL pipeline', prompt: 'Flowchart of a daily ETL pipeline: Source S3 -> Lambda Trigger -> Glue Extract -> Transform -> Redshift Load -> SNS Notify.' },
];

const DEFAULT_MODEL = '@cf/meta/llama-3.3-70b-instruct-fp16-fast';

export const AiDialog = ({ open, onClose, onResult, onOpenSettings }: AiDialogProps) => {
  const api = useAuth((s) => s.api);
  const apiKey = useAuth((s) => s.apiKey);
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [models, setModels] = useState<AiModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !api) return;
    let cancelled = false;
    setModelsLoading(true);
    api
      .listAiModels()
      .then((list) => {
        if (cancelled) return;
        const textModels = list.filter((m) => m.type === 'text');
        setModels(textModels.length ? textModels : list);
        const preferred = textModels.find((m) => m.id === DEFAULT_MODEL) ?? textModels[0] ?? list[0];
        if (preferred) setModel(preferred.id);
      })
      .catch(() => {
        // fall back to default if the model list can't be fetched
      })
      .finally(() => setModelsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, api]);

  if (!open) return null;

  const submit = async () => {
    if (!prompt.trim() || !api) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.generateMermaid(prompt, model, apiKey ?? undefined);
      onResult(res.mermaid);
      onClose();
    } catch (err) {
      const e = err as Error & { details?: { error?: string } };
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur">
      <div className="card w-full max-w-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">
            <Wand2 className="mr-2 inline h-4 w-4 text-archer-300" />
            AI diagram generator
          </h3>
          <button className="tool-btn" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-3 text-xs text-slate-400">
          Describe what you want. The AI responds with raw Mermaid/C4 — you'll see a live preview and can convert it to editable canvas elements when ready.
        </p>

        <div className="mb-3 flex flex-wrap gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className="rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300 hover:bg-slate-700"
              onClick={() => setPrompt(p.prompt)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <label className="label">Model</label>
        <select
          className="input mb-3 text-sm"
          value={model}
          disabled={modelsLoading || models.length === 0}
          onChange={(e) => setModel(e.target.value)}
        >
          {modelsLoading && <option>Loading models…</option>}
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
          {models.length === 0 && !modelsLoading && <option value={DEFAULT_MODEL}>{DEFAULT_MODEL}</option>}
        </select>

        <label className="label">Prompt</label>
        <textarea
          className="input h-28 resize-y"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. C4 Container diagram for a Slack-like chat service with API gateway, message service, presence service, Postgres and Redis…"
        />

        {apiKey ? (
          <p className="mt-3 text-xs text-slate-500">
            AI API key is configured in{' '}
            <button className="text-archer-300 underline" onClick={onOpenSettings}>
              Settings
            </button>
            . Usage charges credits.
          </p>
        ) : (
          <div className="mt-3 rounded border border-amber-700/40 bg-amber-900/20 p-2 text-xs text-amber-200">
            Configure your Omniapps AI API key in{' '}
            <button className="text-archer-300 underline" onClick={onOpenSettings}>
              Settings
            </button>{' '}
            before generating diagrams.
          </div>
        )}

        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}

        <div className="mt-4 flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary flex-1"
            onClick={submit}
            disabled={submitting || !prompt.trim() || !apiKey}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Generate
          </button>
        </div>
      </div>
    </div>
  );
};
